import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { updatePermitPackage } from "../../store/actions/dataActions";
import { setSelectedRow } from "../../store/actions/uiActions/myPermitsActions";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "92vw", sm: 420 },
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: 3,
};

function ChangeNotaryModal({ open, onClose, taskId, signer, currentNotaryId }) {
  const dispatch = useDispatch();
  const currentSession = useSelector((state) => state.sessions.currentSession);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notaries, setNotaries] = useState([]);
  const [selectedNotaryId, setSelectedNotaryId] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setSelectedNotaryId("");
      setError("");
      return;
    }

    const loadNotaries = async () => {
      try {
        setLoading(true);
        setError("");
        const resp = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/nocApi/notary/get-org-notaries`,
          {
            params: { currentSession },
            withCredentials: true,
          },
        );

        setNotaries(resp?.data?.notaries || []);
      } catch (err) {
        setError(
          err?.response?.data?.message ||
            "Failed to load organization notaries.",
        );
        setNotaries([]);
      } finally {
        setLoading(false);
      }
    };

    loadNotaries();
  }, [open, currentSession]);

  const otherNotaries = useMemo(
    () =>
      (notaries || []).filter(
        (notary) =>
          notary?.notary_id &&
          notary?.notary_id !== currentNotaryId &&
          notary?.mongo_id !== currentNotaryId,
      ),
    [notaries, currentNotaryId],
  );

  const handleSubmit = async () => {
    if (!selectedNotaryId) {
      setError("Please select a different notary.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/notary/swap-ipen-notary`,
        {
          task_id: taskId,
          signer,
          new_notary_id: selectedNotaryId,
          currentSession,
        },
        { withCredentials: true },
      );

      if (resp?.data?.success && resp?.data?.data) {
        await dispatch(updatePermitPackage(resp.data.data));
        await dispatch(setSelectedRow(resp.data.data));
        onClose?.();
      }
    } catch (err) {
      setError(
        err?.response?.data?.message || "Failed to change the assigned notary.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={saving ? undefined : onClose}>
      <Box sx={modalStyle}>
        <Typography variant="h6" color="primary" fontWeight="bold" mb={1}>
          Change Notary
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Select another notary from your organization to recreate this IPEN
          session.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Typography variant="body2" color="text.secondary">
            Loading organization notaries...
          </Typography>
        ) : otherNotaries.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            No other notary available so can not swap.
          </Alert>
        ) : (
          <FormControl fullWidth variant="filled">
            <InputLabel id="change-notary-select-label">
              Select Notary
            </InputLabel>
            <Select
              labelId="change-notary-select-label"
              value={selectedNotaryId}
              label="Select Notary"
              onChange={(e) => setSelectedNotaryId(e.target.value)}
            >
              {otherNotaries.map((notary) => (
                <MenuItem key={notary?.notary_id} value={notary?.notary_id}>
                  {notary?.full_name || notary?.first_name || notary?.email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Box
          sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 3 }}
        >
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            onClick={handleSubmit}
            loading={saving}
            disabled={
              loading || otherNotaries.length === 0 || !selectedNotaryId
            }
          >
            Change Notary
          </LoadingButton>
        </Box>
      </Box>
    </Modal>
  );
}

export default ChangeNotaryModal;
